import { useState, useEffect, useCallback, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import AuthService, { fetchWithRetryOn409 } from './auth-service';
import BookingService, {
    Booking,
    BusinessInfo,
    TimeSlot,
    ClientSubscriptionInfo,
    BusinessNotFoundError,
    MaxConcurrentBookingsError,
    UnavailableTimeError,
    PaymentFailedError,
    PaymentJustConfirmedError,
} from './booking-service';
import { BookingApi, Plain, Rich } from '@agendor/shared';
import {
    vars,
    Toast,
    Button,
    ConfirmDialog,
    GhostButton,
    gtagEvent,
    gtagIdentifyClient,
    gtagClearIdentity,
    onlyDigits,
    formatPhone,
    isValidBrazilPhone,
    isValidCpfOrCnpj,
    getTokenFromUrl,
} from '@agendor/ui';
import * as AppRouting from './app-routing';
import { formatDateISO } from './formatting';
import AuthSection, { AuthScreen } from './sections/AuthSection';
import BookingsSection from './sections/BookingsSection';
import BookingDetailsSection from './sections/BookingDetailsSection';
import IntakeSection from './sections/IntakeSection';
import NewBookingSection, { WizardScreen } from './sections/NewBookingSection';

const serverRootPathKey = 'REACT_APP_SERVER_ROOT_PATH';
const _serverRootPath = (window as any).__SERVER_ROOT_PATH__ ?? process.env[serverRootPathKey];
if (_serverRootPath === undefined) {
    throw new Error(`No value found for environment value "${serverRootPathKey}"`);
}
const serverRootPath = _serverRootPath;

const businessSlug = AppRouting.getSlug();
const authService = businessSlug
    ? new AuthService({ serverRootPath, businessSlug, initialAccessToken: getTokenFromUrl() })
    : null;
const bookingService = authService ? new BookingService({ serverRootPath, authService }) : null;
type BusinessLoadError = { type: 'not_found' | 'generic'; message: string };

const BUSINESS_UNAVAILABLE_MESSAGE = 'Agenda indisponível. Fale com o estabelecimento.';

const GlobalStyle = createGlobalStyle`
    :root {
        font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        color: ${vars.foregroundColor};
        background-color: ${vars.backgroundColor};
    }

    *, *::before, *::after {
        box-sizing: border-box;
    }

    html, body, #root {
        margin: 0;
        padding: 0;
        min-height: 100%;
        background-color: ${vars.backgroundColor};
    }
`;

const Page = styled.div`
    min-height: 100vh;
    background: ${vars.backgroundColor};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: ${vars.foregroundColor};

    @media (max-width: 600px) {
        min-height: 100dvh;
        padding: 0;
        align-items: stretch;
    }
`;

const Card = styled.div`
    position: relative;
    background: ${vars.backgroundColorBright};
    border: 1px solid ${vars.dividerColor};
    border-radius: 6px;
    width: min(480px, 100%);
    display: flex;
    flex-direction: column;
    z-index: 1;

    @media (max-width: 600px) {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        border: none;
        border-radius: 0;
        overflow-y: auto;
    }
`;

const CardNavbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    background: rgba(${vars.dangerTintSoftRgb}, 0.95);
    border-bottom: 1px solid ${vars.dividerColor};
    border-radius: 6px 6px 0 0;
    padding: 0 24px;
    min-height: 52px;

    @media (max-width: 600px) {
        min-height: ${vars.navbarHeight};
        border-radius: 0;
    }
`;

const BusinessName = styled.h1`
    font-size: 1rem;
    font-weight: 600;
    margin: 10px 0;
    flex: 1;
    min-width: 0;
`;

const WhatsAppLink = styled(GhostButton)`
    flex-shrink: 0;
`;

const BusinessDescription = styled.div`
    padding: 16px 24px;
    text-align: left;
    font-size: 0.9rem;
    color: #666;
    border-bottom: 1px solid ${vars.dividerColor};
    white-space: pre-line;
`;

const CardBody = styled.div`
    flex: 1;
    padding: 24px;

    h2 {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 8px 0;
    }

    p {
        font-size: 0.9rem;
        color: #888;
        margin: 0 0 16px 0;
    }
`;

const UserBar = styled.div`
    padding: 10px 24px;
    background-color: ${vars.backgroundColorDark};
    border-bottom: 1px solid ${vars.dividerColor};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 0.875rem;
`;

const UserBarButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    border-radius: 6px;
    font: inherit;
    font-size: 0.8rem;
    cursor: pointer;
    background-color: transparent;
    color: ${vars.foregroundColor};
    border: 1px solid ${vars.foregroundColor};

    &:hover {
        background-color: ${vars.primaryColorLight};
    }
`;

const CardFooter = styled.div`
    min-height: ${vars.footerMinHeight};
    padding: 0 24px;
    padding-bottom: env(safe-area-inset-bottom, 0);
    border-top: 1px solid ${vars.dividerColor};
    border-radius: 0 0 6px 6px;
    flex-shrink: 0;
    background-color: ${vars.backgroundColorDark};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    color: #888;
    box-sizing: border-box;

    @media (max-width: 600px) {
        border-radius: 0;
    }
`;

const CardFooterContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: ${vars.footerMinHeight};
`;

const FooterLink = styled.a`
    color: #888;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
        text-decoration: underline;
    }
`;

const FooterLogo = styled.img`
    width: 16px;
    height: 16px;
    opacity: 0.6;
`;

type Screen = AuthScreen | 'bookings' | WizardScreen | 'booking-details' | 'intake';

function collectPendingIntakeIds(
    businessInfo: BookingApi.GetBusinessInfoResponse,
    selectedServiceIds: number[],
): number[] {
    const seen = new Set<number>();
    if (businessInfo.intakes === undefined) return [];
    for (const serviceId of selectedServiceIds) {
        const service = businessInfo.services.find((s) => s.id === serviceId);
        if (!service || service.intakeId == null) continue;
        if (!businessInfo.intakes.some((i) => i.id === service.intakeId)) continue;
        seen.add(service.intakeId);
    }
    return Array.from(seen);
}

export default function App() {
    const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
    const [isBusinessLoading, setIsBusinessLoading] = useState(false);
    const [businessError, setBusinessError] = useState<BusinessLoadError | null>(null);
    const [screen, setScreen] = useState<Screen>('login');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [clientName, setClientName] = useState('');
    const [selectedServices, setSelectedServices] = useState<number[]>([]);
    const [selectedWorker, setSelectedWorker] = useState<number | null>(null);
    const [calendarMonth, setCalendarMonth] = useState<Plain.Month>(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<TimeSlot | null>(null);
    const [bookingNotes, setBookingNotes] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [cpfCnpjError, setCpfCnpjError] = useState<string | null>(null);
    const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [slotsError, setSlotsError] = useState(false);
    const slotsCache = useRef<Map<string, TimeSlot[]>>(new Map());
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [isLoadingBookings, setIsLoadingBookings] = useState(false);
    const [bookingsError, setBookingsError] = useState<string | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        bookingId: string | null;
        serviceName: string;
        isRecurring: boolean;
        isLoading: boolean;
        description?: React.ReactNode;
    }>({
        isOpen: false,
        bookingId: null,
        serviceName: '',
        isRecurring: false,
        isLoading: false,
    });
    const [toast, setToast] = useState<{
        message: string | null;
        type: 'success' | 'error' | 'warning';
        key: number;
        id?: string;
    }>({ message: null, type: 'success', key: 0 });
    const [loginError, setLoginError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [isConfirming, setIsConfirming] = useState(false);
    const [paymentInvoiceUrl, setPaymentInvoiceUrl] = useState<string | null>(null);
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [isBookingActionLoading, setIsBookingActionLoading] = useState(false);
    const [clientSubscription, setClientSubscription] = useState<ClientSubscriptionInfo | null>(null);
    const [cancelSubscriptionDialog, setCancelSubscriptionDialog] = useState<{ isOpen: boolean; isLoading: boolean }>({
        isOpen: false,
        isLoading: false,
    });
    const [lastBookingId, setLastBookingId] = useState<string | null>(null);

    const normalizedPhone = '55' + onlyDigits(phone);

    const tryRefresh = useCallback(async () => {
        if (!authService) {
            setIsCheckingSession(false);
            return;
        }
        if (authService.isAuthenticated) {
            setIsCheckingSession(false);
            return;
        }
        setIsCheckingSession(true);
        try {
            await authService.refreshAccessToken();
        } catch {
            // No session - that's fine, user can still browse and create account
        } finally {
            setIsCheckingSession(false);
        }
    }, []);

    const loadBusinessInfo = useCallback(async (): Promise<BusinessInfo | null> => {
        if (!businessSlug || !bookingService) {
            setBusinessError({ type: 'not_found', message: BUSINESS_UNAVAILABLE_MESSAGE });
            setIsBusinessLoading(false);
            return null;
        }
        setIsBusinessLoading(true);
        setBusinessError(null);
        try {
            const data = await bookingService.getBusinessInfo(businessSlug);
            setBusinessInfo(data);
            if (data.client) {
                setIsAuthenticated(true);
                if (data.client.id !== null) {
                    gtagIdentifyClient({ businessId: data.businessId, clientId: data.client.id });
                }

                if (!data.client.phoneNumber) {
                    setScreen('login');
                } else if (data.client.name) {
                    setScreen('bookings');
                    const subscription = await loadSubscription();
                    loadBookings({ skipToNewIfEmpty: !subscription, workers: data.workers });
                } else {
                    setScreen('name');
                }
            }
            return data;
        } catch (error) {
            if (error instanceof BusinessNotFoundError) {
                setBusinessError({ type: 'not_found', message: BUSINESS_UNAVAILABLE_MESSAGE });
            } else {
                setBusinessError({ type: 'generic', message: 'Não foi possível carregar o estabelecimento.' });
            }
            return null;
        } finally {
            setIsBusinessLoading(false);
        }
    }, []);

    const startNewBookingFlow = useCallback(
        (workers?: { id: number }[]) => {
            const availableWorkers = workers ?? businessInfo?.workers ?? [];
            if (availableWorkers.length === 1) {
                setSelectedWorker(availableWorkers[0].id);
                setScreen('services');
            } else {
                setScreen('worker');
            }
        },
        [businessInfo?.workers],
    );

    const loadBookings = useCallback(
        async (options?: { skipToNewIfEmpty?: boolean; workers?: { id: number }[] }) => {
            if (!businessSlug || !bookingService) return;
            setIsLoadingBookings(true);
            setBookingsError(null);
            try {
                const data = await bookingService.getBookings(businessSlug);
                setBookings(data);
                if (options?.skipToNewIfEmpty && data.length === 0) {
                    startNewBookingFlow(options.workers);
                }
            } catch {
                setBookingsError('Não foi possível carregar os agendamentos.');
            } finally {
                setIsLoadingBookings(false);
            }
        },
        [businessInfo?.workers, startNewBookingFlow],
    );

    const loadSubscription = useCallback(async (): Promise<ClientSubscriptionInfo | null> => {
        if (!businessSlug || !bookingService) return null;
        try {
            const subscription = await bookingService.getSubscription(businessSlug);
            setClientSubscription(subscription);
            return subscription;
        } catch {
            // Non-critical, silently ignore
            return null;
        }
    }, []);

    useEffect(() => {
        (async () => {
            await tryRefresh();
            await loadBusinessInfo();
        })();
    }, [tryRefresh, loadBusinessInfo]);

    useEffect(() => {
        if (!authService) return;
        const subscription = authService.onSessionExpired.subscribe(() => {
            gtagClearIdentity();
            setIsAuthenticated(false);
            setBusinessInfo((prev) => (prev ? { ...prev, client: null } : null));
            setScreen('login');
        });
        return () => subscription.unsubscribe();
    }, []);

    const [intakeBookingId, setIntakeBookingId] = useState<string | null>(null);

    // React to URL changes for booking details and intake screens
    useEffect(() => {
        const handleRouteChange = () => {
            const path = AppRouting.getPath();

            // Check for intake route: /bookings/:bookingId/intake
            const intakeMatch = path.match(/^\/bookings\/([^/]+)\/intake/);
            if (intakeMatch) {
                setIntakeBookingId(intakeMatch[1]);
                setScreen('intake');
                return;
            }

            // Support both /booking/:id and /confirm/:id (backwards compat)
            const match = path.match(/^\/(booking|confirm)\/([^/]+)/);
            const bookingId = match?.[2] ?? null;

            if (bookingId) {
                // Redirect /confirm/:id to /booking/:id
                if (match?.[1] === 'confirm') {
                    AppRouting.navigate(`/booking/${bookingId}`, { replace: true });
                    return;
                }

                const booking = bookings.find((b) => b.id === bookingId);
                if (booking) {
                    setSelectedBooking(booking);
                    setScreen('booking-details');
                } else if (bookings.length > 0) {
                    AppRouting.navigate('/');
                }
            } else if (screen === 'booking-details' || (screen === 'intake' && intakeBookingId)) {
                setSelectedBooking(null);
                setIntakeBookingId(null);
                setScreen('bookings');
                loadBookings();
            }
        };

        handleRouteChange();
        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, [bookings, screen]);

    // Track page views on screen change
    const screenTitles: Record<Screen, string> = {
        login: 'Login',
        code: 'Verificar Código',
        name: 'Seu Nome',
        bookings: 'Meus Agendamentos',
        services: 'Serviços',
        worker: 'Profissional',
        schedule: 'Data e Horário',
        confirm: 'Confirmar',
        success: 'Sucesso',
        intake: 'Formulário',
        'booking-details': 'Detalhes do Agendamento',
    };
    useEffect(() => {
        const pageTitle = `OrganizaBot Agendar | ${screenTitles[screen]}`;
        document.title = pageTitle;
        gtagEvent('page_view', {
            page_title: pageTitle,
            page_location: window.location.href,
            page_path: `/book/${screen}`,
            source_type: 'manual',
        });
    }, [screen]);

    // Auth handlers
    const handleRequestCode = async (e: React.FormEvent) => {
        e.preventDefault();
        gtagEvent('login_send_code_click');
        setLoginError(null);

        if (!isValidBrazilPhone(phone)) {
            setLoginError('Número de telefone inválido.');
            return;
        }

        if (!businessSlug) {
            setLoginError('Estabelecimento não encontrado.');
            return;
        }

        setIsLoading(true);

        try {
            const queryParams = new URLSearchParams({ slug: businessSlug });
            const response = await fetch(`${serverRootPath}/booking/auth/request-code?${queryParams}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phoneNumber: normalizedPhone }),
            });

            if (response.status === 429) {
                setLoginError('Muitas tentativas. Aguarde um momento.');
                return;
            }
            if (!response.ok) {
                setLoginError('Erro ao enviar código. Tente novamente.');
                return;
            }

            setScreen('code');
        } catch {
            setLoginError('Erro de conexão. Verifique sua internet.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        gtagEvent('login_verify_code_click');
        setLoginError(null);

        if (!businessSlug || !authService) {
            setLoginError('Estabelecimento não encontrado.');
            return;
        }

        setIsLoading(true);

        try {
            const queryParams = new URLSearchParams({ slug: businessSlug });
            const response = await fetchWithRetryOn409(() =>
                fetch(`${serverRootPath}/booking/auth/verify-code?${queryParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ phoneNumber: normalizedPhone, code }),
                    credentials: 'include',
                }),
            );

            if (response.status === 401) {
                setLoginError('Código inválido ou expirado.');
                return;
            }
            if (response.status === 429) {
                setLoginError('Muitas tentativas. Aguarde um momento.');
                return;
            }
            if (!response.ok) {
                setLoginError('Erro ao verificar código. Tente novamente.');
                return;
            }

            const { accessToken } = await response.json();
            authService.setAccessToken(accessToken);
            await loadBusinessInfo();
        } catch {
            setLoginError('Erro de conexão. Verifique sua internet.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToPhone = () => {
        gtagEvent('login_back_click');
        setScreen('login');
        setCode('');
        setLoginError(null);
    };

    const handleSaveName = async (e: React.FormEvent) => {
        e.preventDefault();
        gtagEvent('name_save_click');
        if (!businessSlug || !bookingService) return;
        setNameError(null);
        const trimmedName = clientName.trim();
        if (trimmedName.length < 2) {
            setNameError('Nome deve ter pelo menos 2 caracteres.');
            return;
        }
        if (trimmedName.length > 80) {
            setNameError('Nome deve ter no máximo 80 caracteres.');
            return;
        }
        setIsLoading(true);
        try {
            const { id: clientId, accessToken } = await bookingService.setClientName(businessSlug, trimmedName);
            authService?.setAccessToken(accessToken);
            if (businessInfo) {
                gtagIdentifyClient({ businessId: businessInfo.businessId, clientId });
            }
            setBusinessInfo((prev) =>
                prev?.client ? { ...prev, client: { ...prev.client, id: clientId, name: trimmedName } } : prev,
            );
            setScreen('bookings');
            const subscription = await loadSubscription();
            loadBookings({ skipToNewIfEmpty: !subscription, workers: businessInfo?.workers });
        } catch {
            setNameError('Erro ao salvar nome. Tente novamente.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        gtagEvent('login_logout_click');
        gtagClearIdentity();
        AppRouting.navigate('/');
        await authService?.logout();
        setIsAuthenticated(false);
        setBusinessInfo((prev) => (prev ? { ...prev, client: null } : null));
        setPhone('');
        setCode('');
        setClientName('');
        setScreen('login');
    };

    // Booking management callbacks
    const handleCancelBookingClick = (booking: Booking) => {
        gtagEvent('bookings_cancel_click');
        const hasPaidDeposit = booking.payment !== null && booking.payment.paid;
        const deadline = businessInfo?.payment?.transferDeadlineHours ?? null;
        let description: React.ReactNode;
        if (hasPaidDeposit) {
            let text: string;
            if (deadline !== null) {
                const now = new Rich.DateTime(new Date());
                const bookingDateTime = new Rich.DateTime({ date: booking.date, time: booking.time });
                const hoursUntilBooking = now.hoursUntil(bookingDateTime);
                text = hoursUntilBooking >= deadline ? 'O sinal será estornado.' : 'O sinal não será devolvido.';
            } else {
                text = 'O sinal pago não será devolvido.';
            }
            description = <p style={{ marginTop: 8, fontWeight: 500 }}>{text}</p>;
        }
        setConfirmDialog({
            isOpen: true,
            bookingId: booking.id,
            serviceName: booking.services.map((s) => s.name).join(', '),
            isRecurring: booking.isRecurring,
            isLoading: false,
            description,
        });
    };

    const handleCancelBooking = async () => {
        if (!businessSlug || !bookingService || !confirmDialog.bookingId) return;

        setConfirmDialog((prev) => ({ ...prev, isLoading: true }));

        try {
            await bookingService.cancelBooking(businessSlug, confirmDialog.bookingId);
            if (confirmDialog.isRecurring) {
                loadBookings();
            } else {
                setBookings((prev) => prev.filter((b) => b.id !== confirmDialog.bookingId));
            }
            setToast((prev) => ({ message: 'Agendamento cancelado.', type: 'success', key: prev.key + 1 }));

            if (selectedBooking?.id === confirmDialog.bookingId) {
                AppRouting.navigate('/');
            }
        } catch (error) {
            if (error instanceof PaymentJustConfirmedError) {
                setToast((prev) => ({
                    message: 'O pagamento acabou de ser confirmado.',
                    type: 'error',
                    key: prev.key + 1,
                }));
                loadBookings();
            } else {
                setToast((prev) => ({
                    message: 'Não foi possível cancelar o agendamento.',
                    type: 'error',
                    key: prev.key + 1,
                }));
            }
        } finally {
            setConfirmDialog({
                isOpen: false,
                bookingId: null,
                serviceName: '',
                isRecurring: false,
                isLoading: false,
            });
        }
    };

    const handleCancelDialogClose = () => {
        if (confirmDialog.isLoading) return;
        setConfirmDialog({
            isOpen: false,
            bookingId: null,
            serviceName: '',
            isRecurring: false,
            isLoading: false,
        });
    };

    const handleCancelSubscription = async () => {
        if (!businessSlug || !bookingService) return;
        setCancelSubscriptionDialog((prev) => ({ ...prev, isLoading: true }));
        try {
            await bookingService.cancelSubscription(businessSlug);
            gtagEvent('client_subscription_cancelled_by_client');
            setClientSubscription(null);
            setToast((prev) => ({ message: 'Assinatura cancelada.', type: 'success', key: prev.key + 1 }));
            loadBookings();
        } catch {
            setToast((prev) => ({
                message: 'Não foi possível cancelar a assinatura.',
                type: 'error',
                key: prev.key + 1,
            }));
        } finally {
            setCancelSubscriptionDialog({ isOpen: false, isLoading: false });
        }
    };

    // Calendar & schedule
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxAdvanceDays = businessInfo?.booking.maxDaysAhead ?? null;
    const maxDate =
        maxAdvanceDays !== null ? new Rich.SimpleDate(today).adding({ days: maxAdvanceDays }).toDate() : null;

    const services = businessInfo?.services ?? [];
    const workers = businessInfo?.workers ?? [];

    const toggleService = (serviceId: number) => {
        const willSelect = !selectedServices.includes(serviceId);
        gtagEvent('services_toggle_click', { item_id: serviceId, action: willSelect ? 'select' : 'deselect' });
        if (!willSelect) {
            setSelectedServices((prev) => prev.filter((s) => s !== serviceId));
            if (toast.id === 'max_duration') {
                setToast((prev) => ({ ...prev, message: null }));
            }
            return;
        }

        const maxDuration = businessInfo?.booking.maxDurationInMinutes;
        if (maxDuration !== null && maxDuration !== undefined) {
            const service = services.find((s) => s.id === serviceId);
            if (service) {
                const currentDuration = services
                    .filter((s) => selectedServices.includes(s.id))
                    .reduce((sum, s) => sum + s.durationInMinutes, 0);
                if (currentDuration + service.durationInMinutes > maxDuration) {
                    setToast((prev) => ({
                        message: `Duração máxima de ${maxDuration} minutos excedida.`,
                        type: 'warning',
                        key: prev.key + 1,
                        id: 'max_duration',
                    }));
                    return;
                }
            }
        }

        setSelectedServices((prev) => [...prev, serviceId]);
    };

    const prevMonth = () => {
        gtagEvent('schedule_month_nav_click', { action: 'prev' });
        setCalendarMonth((prev) => {
            if (prev.month === 0) {
                return { year: prev.year - 1, month: 11 };
            }
            return { year: prev.year, month: prev.month - 1 };
        });
    };

    const nextMonth = () => {
        gtagEvent('schedule_month_nav_click', { action: 'next' });
        setCalendarMonth((prev) => {
            if (prev.month === 11) {
                return { year: prev.year + 1, month: 0 };
            }
            return { year: prev.year, month: prev.month + 1 };
        });
    };

    const loadSlots = useCallback(
        async (date: Date) => {
            if (!businessSlug || !bookingService || selectedServices.length === 0) return;

            const cacheKey = formatDateISO(date);
            const cached = slotsCache.current.get(cacheKey);
            if (cached) {
                setAvailableSlots(cached);
                return;
            }

            setIsLoadingSlots(true);
            setSlotsError(false);
            try {
                const slots = await bookingService.getAvailability(businessSlug, {
                    date: cacheKey,
                    servicesIds: selectedServices,
                    workerId: selectedWorker ?? undefined,
                });
                slotsCache.current.set(cacheKey, slots);
                setAvailableSlots(slots);
            } catch {
                setSlotsError(true);
                setAvailableSlots([]);
            } finally {
                setIsLoadingSlots(false);
            }
        },
        [selectedServices, selectedWorker],
    );

    const selectDate = (day: number) => {
        const date = new Date(calendarMonth.year, calendarMonth.month, day);
        gtagEvent('schedule_date_click', { label: formatDateISO(date) });
        setSelectedDate(date);
        setSelectedTime(null);
        setAvailableSlots([]);
        setSlotsError(false);
        loadSlots(date);
    };

    const startNewBooking = () => {
        gtagEvent('bookings_new_click');
        setSelectedServices([]);
        setSelectedWorker(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setBookingNotes('');
        setPaymentInvoiceUrl(null);
        setAvailableSlots([]);
        setSlotsError(false);
        if (workers.length === 1) {
            setSelectedWorker(workers[0].id);
            setScreen('services');
        } else {
            setScreen('worker');
        }
    };

    const handleConfirmBooking = async () => {
        gtagEvent('confirm_submit_click');
        if (!businessSlug || !bookingService || !selectedDate || !selectedTime) return;

        if (businessInfo?.payment?.needsCpf) {
            if (!cpfCnpj || !isValidCpfOrCnpj(cpfCnpj)) {
                setCpfCnpjError('Informe um CPF ou CNPJ válido.');
                return;
            }
        }

        setIsConfirming(true);
        try {
            const result = await bookingService.createBooking(businessSlug, {
                workerId: selectedWorker,
                servicesIds: selectedServices,
                date: {
                    year: selectedDate.getFullYear(),
                    month: selectedDate.getMonth() + 1,
                    day: selectedDate.getDate(),
                },
                time: selectedTime,
                notes: bookingNotes,
                cpfCnpj: businessInfo?.payment?.needsCpf ? onlyDigits(cpfCnpj) : undefined,
            });
            setLastBookingId(result.id);
            setPaymentInvoiceUrl(result.payment && !result.payment.paid ? result.payment.invoiceUrl : null);
            if (businessInfo?.payment?.needsCpf && result.payment) {
                setBusinessInfo((prev) =>
                    prev?.payment ? { ...prev, payment: { ...prev.payment, needsCpf: false } } : prev,
                );
            }
            const intakeIds = businessInfo ? collectPendingIntakeIds(businessInfo, selectedServices) : [];
            setScreen(intakeIds.length > 0 ? 'intake' : 'success');
        } catch (error) {
            if (error instanceof MaxConcurrentBookingsError) {
                setToast((prev) => ({
                    message: 'Você atingiu o limite de agendamentos.',
                    type: 'error',
                    key: prev.key + 1,
                }));
            } else if (error instanceof UnavailableTimeError) {
                setToast((prev) => ({
                    message: 'Horário não está mais disponível.',
                    type: 'error',
                    key: prev.key + 1,
                }));
            } else if (error instanceof PaymentFailedError) {
                const paymentErrorMessage = businessInfo?.payment?.needsCpf
                    ? 'Não foi possível processar o pagamento. Verifique o CPF/CNPJ e tente novamente.'
                    : 'Não foi possível processar o pagamento. Tente novamente.';
                setToast((prev) => ({ message: paymentErrorMessage, type: 'error', key: prev.key + 1 }));
            } else {
                setToast((prev) => ({
                    message: 'Não foi possível realizar o agendamento.',
                    type: 'error',
                    key: prev.key + 1,
                }));
            }
        } finally {
            setIsConfirming(false);
        }
    };

    const handleConfirmBookingAction = async () => {
        gtagEvent('presence_confirm_click');
        if (!businessSlug || !bookingService || !selectedBooking) return;

        setIsBookingActionLoading(true);
        try {
            await bookingService.confirmBooking(businessSlug, selectedBooking.id);
            setToast((prev) => ({ message: 'Presença confirmada!', type: 'success', key: prev.key + 1 }));
            AppRouting.navigate('/');
            loadBookings();
        } catch {
            setToast((prev) => ({
                message: 'Não foi possível confirmar a presença.',
                type: 'error',
                key: prev.key + 1,
            }));
        } finally {
            setIsBookingActionLoading(false);
        }
    };

    const handleRejectBookingAction = () => {
        if (!selectedBooking) return;
        setConfirmDialog({
            isOpen: true,
            bookingId: selectedBooking.id,
            serviceName: selectedBooking.services.map((s) => s.name).join(', '),
            isRecurring: selectedBooking.isRecurring,
            isLoading: false,
        });
    };

    const handleViewBookings = () => {
        setSelectedServices([]);
        setSelectedWorker(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setBookingNotes('');
        setScreen('bookings');
        loadBookings();
    };

    const renderNewBookingSection = () => (
        <NewBookingSection
            screen={screen as WizardScreen}
            services={services}
            workers={workers}
            payment={businessInfo?.payment ?? null}
            reminders={businessInfo?.reminders ?? { enabled: false }}
            selectedServices={selectedServices}
            selectedWorker={selectedWorker}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            calendarMonth={calendarMonth}
            availableSlots={availableSlots}
            isLoadingSlots={isLoadingSlots}
            slotsError={slotsError}
            bookingNotes={bookingNotes}
            cpfCnpj={cpfCnpj}
            cpfCnpjError={cpfCnpjError}
            isConfirming={isConfirming}
            paymentInvoiceUrl={paymentInvoiceUrl}
            today={today}
            maxDate={maxDate}
            onToggleService={toggleService}
            onSelectWorker={setSelectedWorker}
            onSelectDate={selectDate}
            onSelectTime={setSelectedTime}
            onPrevMonth={prevMonth}
            onNextMonth={nextMonth}
            onSetBookingNotes={setBookingNotes}
            onSetCpfCnpj={(value) => {
                setCpfCnpj(value);
                setCpfCnpjError(null);
            }}
            onConfirmBooking={handleConfirmBooking}
            onViewBookings={handleViewBookings}
            onLoadSlots={loadSlots}
            onNavigate={(s) => setScreen(s as Screen)}
            onBackFromServices={() => {
                if (workers.length === 1) {
                    setSelectedWorker(null);
                    setSelectedServices([]);
                    setScreen('bookings');
                } else {
                    setScreen('worker');
                }
            }}
            onContinueFromServices={() => {
                slotsCache.current.clear();
                setSelectedDate(null);
                setSelectedTime(null);
                setAvailableSlots([]);
                setScreen('schedule');
            }}
            onContinueFromWorker={() => {
                setSelectedServices([]);
                setScreen('services');
            }}
            wizardIntakes={
                businessInfo
                    ? collectPendingIntakeIds(businessInfo, selectedServices)
                          .map((id) => businessInfo.intakes?.find((i) => i.id === id))
                          .filter((i): i is BookingApi.Intake => i !== undefined)
                    : []
            }
            lastBookingId={lastBookingId}
            businessSlug={businessSlug!}
            bookingService={bookingService!}
        />
    );

    const renderContent = () => {
        switch (screen) {
            case 'login':
            case 'code':
            case 'name':
                return (
                    <AuthSection
                        screen={screen}
                        phone={phone}
                        code={code}
                        clientName={clientName}
                        loginError={loginError}
                        nameError={nameError}
                        isLoading={isLoading}
                        onPhoneChange={(value) => {
                            setPhone(value);
                            setLoginError(null);
                        }}
                        onCodeChange={(value) => {
                            setCode(value);
                            setLoginError(null);
                        }}
                        onClientNameChange={setClientName}
                        onRequestCode={handleRequestCode}
                        onVerifyCode={handleVerifyCode}
                        onBackToPhone={handleBackToPhone}
                        onSaveName={handleSaveName}
                    />
                );

            case 'bookings':
                return (
                    <BookingsSection
                        bookings={bookings}
                        bookingsError={bookingsError}
                        isLoadingBookings={isLoadingBookings}
                        clientSubscription={clientSubscription}
                        onNewBooking={startNewBooking}
                        onCancelBooking={handleCancelBookingClick}
                        onCancelSubscription={() => setCancelSubscriptionDialog({ isOpen: true, isLoading: false })}
                        onRetryLoadBookings={() => loadBookings()}
                    />
                );

            case 'booking-details':
                if (!selectedBooking) {
                    setScreen('bookings');
                    return null;
                }
                return (
                    <BookingDetailsSection
                        booking={selectedBooking}
                        services={services}
                        isBookingActionLoading={isBookingActionLoading}
                        onConfirmBooking={handleConfirmBookingAction}
                        onCancelBooking={handleRejectBookingAction}
                    />
                );

            case 'intake':
                if (intakeBookingId && bookingService) {
                    return (
                        <IntakeSection
                            bookingService={bookingService}
                            businessSlug={businessSlug!}
                            bookingId={intakeBookingId}
                            intakes={null}
                            onViewBookings={() => AppRouting.navigate('/')}
                            onComplete={null}
                            onSkip={null}
                        />
                    );
                }
                // Wizard intake step — render via NewBookingSection
                return renderNewBookingSection();

            case 'worker':
            case 'services':
            case 'schedule':
            case 'confirm':
            case 'success':
                return renderNewBookingSection();
        }
    };

    const businessWhatsapp = businessInfo?.whatsappPhoneNumber;

    return (
        <>
            <GlobalStyle />
            <Page>
                <Card id="app-card">
                    <CardNavbar>
                        <BusinessName>
                            {businessInfo?.name ?? (businessError ? 'Agendamento' : 'Carregando...')}
                        </BusinessName>
                        {businessWhatsapp && (
                            <WhatsAppLink
                                as="a"
                                href={`https://wa.me/${businessWhatsapp}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => gtagEvent('whatsapp_click')}
                            >
                                WhatsApp
                            </WhatsAppLink>
                        )}
                    </CardNavbar>
                    {screen === 'bookings' && businessInfo?.description && (
                        <BusinessDescription>{businessInfo.description}</BusinessDescription>
                    )}
                    {isBusinessLoading && <UserBar>Carregando estabelecimento...</UserBar>}
                    {isAuthenticated && (
                        <UserBar data-testid="user-info">
                            <span>
                                {businessInfo?.client?.name ? (
                                    <>
                                        Agendando como <strong>{businessInfo.client.name}</strong>
                                    </>
                                ) : (
                                    <>
                                        Logado como{' '}
                                        <strong>
                                            {formatPhone(businessInfo?.client?.phoneNumber?.slice(2) ?? '')}
                                        </strong>
                                    </>
                                )}
                            </span>
                            <UserBarButton onClick={handleLogout} data-testid="switch-user-btn">
                                {businessInfo?.client?.name ? 'Trocar' : 'Sair'}
                            </UserBarButton>
                        </UserBar>
                    )}
                    <CardBody>
                        {isCheckingSession || isBusinessLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>Carregando...</div>
                        ) : businessError ? (
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '40px 0',
                                    color: '#888',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    alignItems: 'center',
                                }}
                                data-testid="business-error"
                            >
                                <div>{businessError.message}</div>
                                {businessError.type !== 'not_found' && (
                                    <Button onClick={loadBusinessInfo}>Tentar de novo</Button>
                                )}
                            </div>
                        ) : (
                            renderContent()
                        )}
                    </CardBody>
                    <CardFooter id="app-footer">
                        <CardFooterContent>
                            <span>Agendamento por</span>
                            <FooterLink
                                href="https://organizabot.com"
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => gtagEvent('footer_brand_click')}
                            >
                                <FooterLogo src="/logo_60x60.png" alt="" />
                                OrganizaBot
                            </FooterLink>
                        </CardFooterContent>
                    </CardFooter>
                </Card>
            </Page>
            <Toast
                message={toast.message}
                type={toast.type}
                toastKey={toast.key}
                onClose={() => setToast((prev) => ({ ...prev, message: null }))}
                footerId="app-footer"
                containerId="app-card"
            />
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title="Cancelar agendamento"
                message={
                    <>
                        Deseja cancelar o agendamento de &quot;{confirmDialog.serviceName}&quot;?
                        {confirmDialog.isRecurring && (
                            <p style={{ marginTop: 8 }} data-testid="cancel-recurring-note">
                                Este é um agendamento recorrente: só a próxima ocorrência será cancelada. Para encerrar
                                a recorrência, fale com o estabelecimento.
                            </p>
                        )}
                        {confirmDialog.description}
                    </>
                }
                confirmText="Cancelar"
                cancelText="Voltar"
                onConfirm={handleCancelBooking}
                onCancel={handleCancelDialogClose}
                isLoading={confirmDialog.isLoading}
            />
            <ConfirmDialog
                isOpen={cancelSubscriptionDialog.isOpen}
                title="Cancelar assinatura"
                message="Tem certeza? Agendamentos vinculados serão cancelados."
                confirmText="Cancelar"
                cancelText="Fechar"
                onConfirm={handleCancelSubscription}
                onCancel={() => setCancelSubscriptionDialog({ isOpen: false, isLoading: false })}
                isLoading={cancelSubscriptionDialog.isLoading}
            />
        </>
    );
}
